import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../common/style.css';
import SideNave from '../common/sidenav';
import TopNav from '../common/topnav';
import 'bootstrap/dist/css/bootstrap.min.css';
import Loading from "../loading/loading";
import { createCV, uploadFile, getCVDataByID } from "../http/api";
import { useAuth } from "../http/authcontex";
import { FaRegSave } from 'react-icons/fa';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { businessVerticalList } from "../http/api";
import { jobdesignaction } from "../http/api";



export default function AddCVs() {
    // Hooks and state initialization
    const authContext = useAuth();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const { userEmail } = useAuth();
    const location = useLocation();

    // State for sidebar collapse
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
        localStorage.getItem('sidebarCollapsed') === 'true'
    );

    // Toggle sidebar function
    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
        localStorage.setItem('sidebarCollapsed', !isSidebarCollapsed);
    };

    // State for form data
    const [formData, setFormData] = useState({
        id: 0,
        vertical: '',
        jobdesignation: '',
        name: '',
        fileName: '',
        tag: '',
        submittedBy: ''
    });

    // Dropdown options state
    const [verticalOptions, setVerticalOptions] = useState([]);
    const [jobDesignationOptions, setJobDesignationOptions] = useState([]);

    // Handle input change for text fields
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle dropdown change
    const handleDropdownChange = (selectedOption, { name }) => {
        setFormData((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : "" }));
    };

    // --- File Upload Logic ---
    const handleFileUpload = useCallback(
        async (file) => {
            if (!file) return;

            const allowedTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/msword',
                'application/pdf'
            ];

            if (!allowedTypes.includes(file.type)) {
                toast.error("Invalid file type. Only Excel, Word, and PDF are allowed.");
                return;
            }

            const fileFormData = new FormData();
            const keyName = `upload_${Date.now()}_${file.name}`;
            fileFormData.append("file", file);
            fileFormData.append("keyName", keyName);

            setLoading(true);
            try {
                const response = await uploadFile(fileFormData);
                if (response.status === 200 || response.status === 202) {
                    setFormData(prev => ({ ...prev, fileName: keyName }));
                    toast.success("File uploaded successfully!");
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    authContext.logout();
                    navigate("");
                } else {
                    toast.error("File upload failed!");
                }
            } finally {
                setLoading(false);
            }
        },
        [authContext, navigate]
    );

    // Fetch vertical options for dropdown
    const fetchVerticalOptions = useCallback(() => {
        businessVerticalList()
            .then((response) => {
                if (response.status === 200) {
                    const options = response.data.map((v) => ({
                        value: v.name,
                        label: v.name,
                    }));
                    setVerticalOptions(options);
                }
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    authContext.logout();
                    navigate("");
                }
            });
    }, [authContext, navigate]);

    // Fetch job designation options for dropdown
    const fetchJobDesignations = useCallback(() => {
        jobdesignaction()
            .then((response) => {
                if (response.status === 200 && Array.isArray(response.data)) {
                    const options = response.data.map((j) => ({
                        value: j.name || j.designationName || j.id,
                        label: j.name || j.designationName || j.id,
                    }));
                    setJobDesignationOptions(options);
                }
            })
            .catch((error) => {
                console.error("Job Designation API Error:", error);
                if (error.response?.status === 401) {
                    authContext.logout();
                    navigate("");
                }
            });
    }, [authContext, navigate]);

    // Fetch CV data by ID when location state changes
    const getCVDetailsByID = useCallback(async (cvId, type) => {
        setLoading(true);
        try {
            const response = await getCVDataByID(cvId);
            if (response.status === 200 || response.status === 201) {
                const fetchedData = response.data;
                setFormData(prev => {
                    let updatedData = { ...fetchedData };
                    if (type === 'COPY') updatedData.id = 0;
                    return updatedData;
                });
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                authContext.logout();
                navigate("");
            }
        } finally {
            setLoading(false);
        }
    }, [authContext, navigate]);

    // Effects to fetch data on component mount
    useEffect(() => {
        fetchJobDesignations();
        fetchVerticalOptions();
    }, [fetchJobDesignations, fetchVerticalOptions]);

    useEffect(() => {
        if (location.state && location.state.cv) {
            getCVDetailsByID(location.state.cv.id, location.state.typ);
        }
    }, [location.state, getCVDetailsByID]);

    // File input change handler
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) handleFileUpload(file);
    };

    // --- Validation Logic ---
    const validate = () => {
        let validationErrors = {};
        if (!formData.vertical) validationErrors.vertical = "Vertical is required";
        if (!formData.jobdesignation) validationErrors.jobdesignation = "Job Designation is required";
        if (!formData.name) validationErrors.name = "Name is required";
        if (!formData.fileName) validationErrors.fileName = "CV File is required";
        return validationErrors;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            toast.error("Please fill in all required fields.");
            return;
        }

        const submissionData = { ...formData, submittedBy: userEmail };
        setLoading(true);

        try {
            const response = await createCV(submissionData);
            if (response.status >= 200 && response.status <= 201) {
                navigate('/cvs', { state: { success: true } });
            }
        } catch (error) {
            toast.error("Error saving data!");
            if (error.response && error.response.data) {
                setErrors(error.response.data);
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className={`sb-nav-fixed ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            {loading && <Loading />}
            <TopNav toggleSidebar={toggleSidebar} />
            <div id="layoutSidenav">
                <SideNave isSidebarCollapsed={isSidebarCollapsed} toggleSidebar={toggleSidebar} />
                <div id="layoutSidenav_content">
                    <main>
                        <div className="container-fluid px-4">
                            <br />
                            <h6 className="mt-2">CV Details</h6>
                            <div className="card mb-4 border-0 shadow-sm rounded-3">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-3">
                                            {/* Vertical Dropdown */}
                                            <div className="col-md-6 col-12">
                                                <label className="form-label">Vertical <span className="text-danger">*</span></label>
                                                <Select
                                                    name="vertical"
                                                    value={verticalOptions.find((opt) => opt.value === formData.vertical) || null}
                                                    onChange={handleDropdownChange}
                                                    options={verticalOptions}
                                                    isClearable
                                                    classNamePrefix="react-select"
                                                    placeholder="Select Vertical"
                                                />
                                                {errors.vertical && <div className="text-danger small">{errors.vertical}</div>}
                                            </div>

                                            {/* Name Input */}
                                            <div className="col-md-6 col-12">
                                                <label className="form-label">Name <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
                                                    name="name"
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                />
                                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                            </div>

                                            {/* Job Designation Dropdown */}
                                            <div className="col-md-6 col-12">
                                                <label className="form-label">Job Designation<span className="text-danger"> *</span></label>
                                                <Select
                                                    name="jobdesignation"
                                                    value={jobDesignationOptions.find(opt => opt.value === formData.jobdesignation) || null}
                                                    onChange={handleDropdownChange}
                                                    options={jobDesignationOptions}
                                                    classNamePrefix="react-select"
                                                    placeholder="Select Designation"
                                                />
                                                {errors.jobdesignation && <div className="text-danger small">{errors.jobdesignation}</div>}
                                            </div>

                                            {/* File Upload Input */}
                                            <div className="col-md-6 col-12">
                                                <label className="form-label">Upload CV <span className="text-danger">*</span></label>
                                                <input
                                                    type="file"
                                                    className={`form-control form-control-sm ${errors.fileName ? 'is-invalid' : ''}`}
                                                    onChange={handleFileChange}
                                                    accept=".xlsx,.xls,.docx,.doc,.pdf"
                                                />
                                                {errors.fileName && <div className="invalid-feedback">{errors.fileName}</div>}
                                            </div>

                                            {/* Tag Textarea */}
                                            <div className="col-md-6 col-12">
                                                <label className="form-label">Tag</label>
                                                <textarea
                                                    className="form-control form-control-sm"
                                                    name="tag"
                                                    value={formData.tag}
                                                    onChange={handleChange}
                                                    rows="2"
                                                ></textarea>
                                            </div>
                                        </div>

                                        {/* Save button */}
                                        <div className="d-flex justify-content-end mt-3">
                                            <button type="submit" className="btn-submit">
                                                Save <FaRegSave className="ms-1" />
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
}
